import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  Input,
  Text,
} from '@chakra-ui/react'
import { map, omit, some, capitalize } from 'lodash'
import React from 'react'
import { RiInformationLine } from 'react-icons/ri'
import type { Abi, AbiFunction } from 'viem'
import { parseEther, getAddress, getContract } from 'viem'
import { waitForTransactionReceipt } from 'viem/actions'
import { useAccount, useSwitchChain } from 'wagmi'
import { z, ZodError } from 'zod'
import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { ConnectButton } from 'components/ConnectButton'
import { Label } from 'components/Label'
import { zircuit } from 'constants/network'
import { Routes } from 'constants/routes'
import { useThemeColors } from 'hooks/useThemeColors'
import { createWalletClientL2 } from 'lib/viem'
import { getParsedContractArgument } from 'utils/getParsedContractArgument'

const payableAmountSchema = z.object({
  payableAmount: z.string().superRefine((val, ctx) => {
    if (val === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Payable amount is required',
      })
      return z.NEVER
    }

    const num = Number(val)

    if (Number.isNaN(num) || num <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Payable amount must be a positive number',
      })
      return z.NEVER
    }

    return z.NEVER
  }),
})

type WriteContractProps = {
  data: AbiFunction[]
  hash: string
  title?: React.ReactNode
}

export const WriteContract = ({ title, data, hash }: WriteContractProps) => {
  const COLORS = useThemeColors()
  const [inputStates, setInputStates] = React.useState<Record<number, Record<number, string>>>({})
  const [errors, setErrors] = React.useState<Record<number, string>>({})
  const [loadingQueryIndexes, setLoadingQueryIndexes] = React.useState<number[]>([])
  const [transactionHashes, setTransactionHashes] = React.useState<Record<number, string>>({})
  const [payableAmounts, setPayableAmounts] = React.useState<Record<number, string>>({})

  const { isConnected, chainId } = useAccount()
  const { switchChain } = useSwitchChain()

  const isCorrectNetwork = chainId === zircuit.id

  return (
    <>
      {title}
      {data.length > 0 ? (
        <>
          {!isConnected && (
            <Flex gap={3} mb={4}>
              <ConnectButton />
              <Label variant="information" icon={<RiInformationLine />}>
                You need to be connected to be able to write to contract.
              </Label>
            </Flex>
          )}
          <Accordion allowMultiple>
            {data.map(({ inputs, name, stateMutability }, methodIndex) => (
              <AccordionItem
                key={`${name ?? ''}-${methodIndex.toString()}-write`}
                bgColor={COLORS.grey07}
                _notFirst={{ marginTop: 4 }}
                border="none"
                borderRadius={8}
              >
                <AccordionButton py={4} borderRadius={8}>
                  <Text w="full" textAlign="start" variant="title2medium">
                    {methodIndex + 1}. {name}
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <Divider mb={3} />
                  {stateMutability === 'payable' && (
                    <Box my={4}>
                      <Text mb={2} variant="caption1regular" color={COLORS.grey02}>
                        send
                      </Text>
                      <Input
                        w="65%"
                        placeholder="payableAmount (in ETH)"
                        value={payableAmounts[methodIndex] ?? ''}
                        onChange={(event) => {
                          setPayableAmounts((prev) => ({
                            ...prev,
                            [methodIndex]: event.target.value,
                          }))
                        }}
                      />
                    </Box>
                  )}
                  {inputs?.map((input, inputIndex) => {
                    const type =
                      'components' in input
                        ? `[${input.components.map((component) => component.type).join(',')}]`
                        : input.type
                    const inputNameAndType = `${input.name || '<input>'} (${type})`

                    return (
                      <Box
                        my={4}
                        key={`${input.name ?? ''}-${type ?? ''}-${inputIndex.toString()}-write`}
                      >
                        <Text mb={2} variant="caption1regular" color={COLORS.grey02}>
                          {inputNameAndType}
                        </Text>
                        <Input
                          value={inputStates[methodIndex]?.[inputIndex] ?? ''}
                          w="65%"
                          onChange={(event) =>
                            setInputStates((prev) => ({
                              ...prev,
                              [methodIndex]: {
                                ...prev[methodIndex],
                                [inputIndex]: event.target.value,
                              },
                            }))
                          }
                          placeholder={inputNameAndType}
                        />
                      </Box>
                    )
                  })}
                  {errors[methodIndex] && (
                    <Alert mb={4} w="fit-content" variant="error">
                      {errors[methodIndex]}
                    </Alert>
                  )}
                  <Flex gap={1}>
                    <Button
                      isLoading={loadingQueryIndexes.includes(methodIndex)}
                      isDisabled={
                        !isConnected
                          ? true
                          : some(map(inputStates[methodIndex], (value) => value === '')) ||
                            Object.keys(inputStates[methodIndex] ?? {}).length !== inputs?.length
                      }
                      onClick={() => {
                        const asyncFn = async () => {
                          try {
                            // Clear previous transaction hash when starting new transaction
                            setTransactionHashes((prev) => omit(prev, methodIndex))

                            const args = Object.values(inputStates[methodIndex] ?? {}).map(
                              (arg: string) => {
                                // Handle array inputs that start and end with brackets
                                if (arg.trim().startsWith('[') && arg.trim().endsWith(']')) {
                                  return arg
                                    .trim()
                                    .slice(1, -1)
                                    .split(',')
                                    .map((item) => getParsedContractArgument(item))
                                }

                                return getParsedContractArgument(arg)
                              }
                            )

                            if (stateMutability === 'payable') {
                              setErrors((prev) => omit(prev, methodIndex))
                              payableAmountSchema.parse({
                                payableAmount: payableAmounts[methodIndex],
                              })
                            }

                            setLoadingQueryIndexes((prev) => [...prev, methodIndex])

                            if (name) {
                              const walletClient = await createWalletClientL2()

                              const contract = getContract({
                                address: getAddress(hash),
                                abi: data as Abi,
                                client: walletClient!,
                              })

                              const options = {
                                ...(stateMutability === 'payable'
                                  ? {
                                      value: parseEther(payableAmounts[methodIndex] ?? '0'),
                                    }
                                  : {}),
                              }

                              const estimatedGas = await contract.estimateGas[name](
                                args.filter((value) => value !== ''),
                                options
                              )

                              const tx = await contract.write[name](
                                args.filter((value) => value !== ''),
                                {
                                  ...options,
                                  // *: Can't multiply by 1.50 because of underflow
                                  // *: Increase gas limit by 50% for the transaction to not fail
                                  gasLimit: (estimatedGas * 150n) / 100n,
                                }
                              )

                              // Wait for the transaction to be mined
                              await waitForTransactionReceipt(walletClient!, {
                                hash: tx,
                              })

                              setErrors((prev) => omit(prev, methodIndex))
                              setTransactionHashes((prev) => ({
                                ...prev,
                                [methodIndex]: tx,
                              }))
                            } else {
                              throw new Error('No method name provided')
                            }
                          } catch (error) {
                            if (error instanceof ZodError) {
                              setErrors((prev) => ({
                                ...prev,
                                [methodIndex]: error.issues[0].message,
                              }))
                            } else if (error instanceof Error) {
                              if (error.name === 'ContractFunctionExecutionError') {
                                setErrors((prev) => ({
                                  ...prev,
                                  [methodIndex]:
                                    'RPC request failed. Please check if everything is set properly and try again.',
                                }))
                              } else {
                                setErrors((prev) => ({
                                  ...prev,
                                  [methodIndex]:
                                    'shortMessage' in error &&
                                    typeof error.shortMessage === 'string'
                                      ? capitalize(error.shortMessage)
                                      : error.message,
                                }))
                              }
                            }
                          } finally {
                            setLoadingQueryIndexes((prev) =>
                              prev.filter((queryIndex) => queryIndex !== methodIndex)
                            )
                          }
                        }

                        if (!isCorrectNetwork) {
                          setLoadingQueryIndexes((prev) => [...prev, methodIndex])
                          switchChain(
                            {
                              chainId: zircuit.id,
                            },
                            {
                              onSettled: (_, error) => {
                                if (!error) {
                                  void asyncFn()
                                } else {
                                  setLoadingQueryIndexes((prev) =>
                                    prev.filter((queryIndex) => queryIndex !== methodIndex)
                                  )
                                }
                              },
                            }
                          )
                        } else {
                          void asyncFn()
                        }
                      }}
                      size="small"
                    >
                      Write
                    </Button>
                    {transactionHashes[methodIndex] && (
                      <Button
                        size="small"
                        onClick={() => {
                          window.open(
                            Routes.transactionDetail({
                              txHash: transactionHashes[methodIndex],
                              activeTab: 0,
                            }),
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }}
                      >
                        View your transaction
                      </Button>
                    )}
                  </Flex>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </>
      ) : (
        <Alert variant="information">
          Sorry, no public Write methods were found for this contract.
        </Alert>
      )}
    </>
  )
}
